/**
 * Componente DataTable - ConfereAI
 *
 * Tabela de dados padronizada para listagens.
 * Baseado no layout da página de Veículos.
 */

import React from 'react';
import './DataTable.css';

export interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
  render?: (value: any, row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  /** Referência para o elemento sentinela do infinite scroll */
  sentinelRef?: (node: HTMLElement | null) => void;
  /** Indica se está carregando mais itens (infinite scroll) */
  isLoadingMore?: boolean;
}

function DataTable<T extends { id: number | string }>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'Nenhum registro encontrado',
  className = '',
  sentinelRef,
  isLoadingMore = false,
}: DataTableProps<T>) {
  const getCellValue = (row: T, column: Column<T>): React.ReactNode => {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    return row[column.accessor] as React.ReactNode;
  };

  if (data.length === 0) {
    return (
      <div className="table-empty-state">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className={`data-table ${className}`}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index} className={column.className}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(row)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns.map((column, index) => {
                const value = getCellValue(row, column);
                return (
                  <td key={index} className={column.className}>
                    {column.render ? column.render(value, row) : value}
                  </td>
                );
              })}
            </tr>
          ))}
          {/* Loading row para infinite scroll */}
          {isLoadingMore && (
            <tr className="loading-more-row">
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: '20px' }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                Carregando mais resultados...
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {/* Elemento sentinela para infinite scroll */}
      {sentinelRef && <div ref={sentinelRef} style={{ height: '20px' }} />}
    </div>
  );
}

export default DataTable;
